import { useEffect, useRef, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { IconRefresh } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import {
  HISTORICAL_STATUSES,
  LeadStatus,
  ONBOARDING_STATUSES,
  OnboardingStatus,
} from "../../../types/onboarding";
import CancellationDrawer from "./CancellationDrawer";
import CancellationRequestDrawer from "./CancellationRequestDrawer";
import ChooseCancellationTypeModal from "./ChooseCancellationTypeModal";
import ClaimDrawer from "./ClaimDrawer";

const isTipo1Eligible = (status: LeadStatus): boolean =>
  status === "complete" || status === "insurer_review";

type Props = {
  leadUlid: string;
  currentStatus: LeadStatus;
  onStatusChange: (newStatus: LeadStatus) => void;
};

const LABELS: Record<string, string> = {
  insurer_review: "Insurer review",
  docs_pending: "Docs pending",
  signature_pending: "Signature pending",
  ops_review: "Ops review",
  it_review: "IT review",
  csv_pending: "CSV pending",
  legalnote_pending: "Legal note pending",
  payment_validation_pending: "Payment validation",
  ready_to_send: "Ready to send",
  complete: "Complete",
  canceled: "Canceled",
};

export default function StatusChangeButton({
  leadUlid,
  currentStatus,
  onStatusChange,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [claimDrawerOpen, setClaimDrawerOpen] = useState(false);
  const [cancellationDrawerOpen, setCancellationDrawerOpen] = useState(false);
  const [cancellationRequestDrawerOpen, setCancellationRequestDrawerOpen] =
    useState(false);
  const [cancellationChooserOpen, setCancellationChooserOpen] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(
    null,
  );

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setError(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const submit = async (target: string) => {
    if (submitting) return;
    if (target === "claim") {
      setOpen(false);
      setClaimDrawerOpen(true);
      return;
    }
    if (target === "canceled") {
      setOpen(false);
      // When the lead is in a Tipo-1-eligible status (complete / insurer_review)
      // ask the asesora which type of cancellation; otherwise go straight to
      // the immediate flow (Tipos 2/3 are the only valid options there).
      if (isTipo1Eligible(currentStatus)) {
        setCancellationChooserOpen(true);
      } else {
        setCancellationDrawerOpen(true);
      }
      return;
    }
    if (target === "insurer_review") {
      const ok = await confirm({
        title: "Enviar a la aseguradora",
        message:
          "Se enviarán a la aseguradora los documentos del lead (DNI, contrato, datasheet) y el lead pasará a estado Insurer review. ¿Continuar?",
        confirmLabel: "Enviar",
        tone: "warning",
      });
      if (!ok) return;
    } else {
      const targetLabel = LABELS[target] ?? target;
      const currentLabel = LABELS[currentStatus] ?? currentStatus;
      const ok = await confirm({
        title: "Cambiar estado del lead",
        message:
          `El lead pasará de "${currentLabel}" a "${targetLabel}". ¿Continuar?`,
        confirmLabel: "Cambiar",
        tone: "primary",
      });
      if (!ok) return;
    }
    setSubmitting(target);
    setError(null);
    try {
      if (target === "insurer_review") {
        // Mirror the legacy "Send to insurer" action: gathers the lead's
        // documents, emails them to the carrier and transitions to
        // insurer_review server-side. A plain status PATCH would skip
        // the email entirely.
        await request<{ sent: boolean }>(
          `/api/admin/operations/leads/${leadUlid}/send-to-insurer`,
          { method: "POST", body: {}, token },
        );
      } else {
        await request<{ status: string; changed: boolean }>(
          `/api/admin/operations/onboarding/${leadUlid}/status`,
          { method: "PATCH", body: { status: target }, token },
        );
      }
      onStatusChange(target as LeadStatus);
      setOpen(false);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(`HTTP ${err.status}`);
      } else {
        setError("No se pudo cambiar");
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-block" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={openMenu}
        title="Cambiar estado"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "1.9rem",
          height: "1.9rem",
          padding: 0,
          borderRadius: "0.4rem",
          background: open ? "#0069d9" : "#f8f9fb",
          color: open ? "white" : "#1f2933",
          border: "1px solid #dee2e6",
          fontSize: ".95rem",
          cursor: "pointer",
        }}
      >
        <IconRefresh />
      </button>

      {open && menuPos && (
        <div
          style={{
            position: "fixed",
            top: menuPos.top,
            right: menuPos.right,
            zIndex: 150,
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: "0.5rem",
            boxShadow: "0 6px 16px rgba(0,0,0,.12)",
            minWidth: 200,
            padding: "0.4rem",
          }}
        >
          <div
            style={{
              fontSize: ".7rem",
              textTransform: "uppercase",
              color: "#73879C",
              fontWeight: 600,
              padding: "0.3rem 0.5rem 0.5rem",
            }}
          >
            Cambiar a
          </div>
          {ONBOARDING_STATUSES.map((s) => (
            <StatusOption
              key={s}
              status={s}
              label={LABELS[s] ?? s}
              disabled={s === currentStatus}
              submitting={submitting === s}
              onSelect={() => submit(s)}
            />
          ))}
          <div
            style={{
              borderTop: "1px solid #f1f3f5",
              margin: "0.3rem 0",
            }}
          />
          {HISTORICAL_STATUSES.map((s) => (
            <StatusOption
              key={s}
              status={s}
              label={LABELS[s] ?? s}
              disabled={false}
              submitting={submitting === s}
              danger={s === "canceled"}
              onSelect={() => submit(s)}
            />
          ))}
          {error && (
            <div
              style={{
                padding: "0.4rem 0.5rem",
                fontSize: ".75rem",
                color: "#b32424",
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}
      {claimDrawerOpen && (
        <ClaimDrawer
          leadUlid={leadUlid}
          currentStatus={currentStatus}
          onClose={() => setClaimDrawerOpen(false)}
          onApplied={() => {
            setClaimDrawerOpen(false);
            onStatusChange("claim");
          }}
        />
      )}
      {cancellationDrawerOpen && (
        <CancellationDrawer
          leadUlid={leadUlid}
          onClose={() => setCancellationDrawerOpen(false)}
          onApplied={() => {
            setCancellationDrawerOpen(false);
            onStatusChange("canceled");
          }}
        />
      )}
      {cancellationRequestDrawerOpen && (
        <CancellationRequestDrawer
          leadUlid={leadUlid}
          onClose={() => setCancellationRequestDrawerOpen(false)}
          onApplied={() => {
            setCancellationRequestDrawerOpen(false);
            // The lead stays in its current status until the request is
            // signed and completed; we just refresh the parent so the
            // pending-request badge appears.
            onStatusChange(currentStatus);
          }}
        />
      )}
      {cancellationChooserOpen && (
        <ChooseCancellationTypeModal
          onClose={() => setCancellationChooserOpen(false)}
          onChoose={(type) => {
            setCancellationChooserOpen(false);
            if (type === "immediate") {
              setCancellationDrawerOpen(true);
            } else {
              setCancellationRequestDrawerOpen(true);
            }
          }}
        />
      )}
    </div>
  );
}

function StatusOption({
  label,
  disabled,
  submitting,
  danger,
  onSelect,
}: {
  status: string;
  label: string;
  disabled: boolean;
  submitting: boolean;
  danger?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled || submitting}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "0.4rem 0.5rem",
        borderRadius: "0.35rem",
        background: "transparent",
        color: disabled ? "#adb5bd" : danger ? "#b32424" : "#1f2933",
        border: "none",
        fontSize: ".82rem",
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = "#f8f9fb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {submitting ? "…" : label}
      {disabled && " (actual)"}
    </button>
  );
}
